<template>
  <div class="historical-metrics-dashboard">
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular
        indeterminate
        color="primary"
        size="48"
        class="mb-4"
      ></v-progress-circular>
      <p class="text-lg font-medium text-gray-700 mb-2">
        Loading Historical Data...
      </p>
      <p class="text-sm text-gray-500">Fetching metrics across time periods</p>
    </div>

    <!-- No Users Selected State -->
    <div v-else-if="selectedUsers.length === 0" class="no-data-container">
      <v-icon size="64" color="grey">mdi-chart-timeline-variant</v-icon>
      <p class="no-data-text">
        Please select users to view their historical trends data
      </p>
    </div>

    <!-- Main Content -->
    <div v-else class="space-y-6">
      <!-- Selected Date Indicator -->
      <div
        v-if="selectedDate"
        class="bg-blue-50 rounded-lg shadow-sm p-4 border border-blue-200"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <v-icon color="blue" class="mr-2">mdi-calendar</v-icon>
            <span class="text-lg font-semibold text-blue-800">
              Viewing Historical Data for: {{ formatDate(selectedDate) }}
            </span>
          </div>
          <v-chip color="blue" outlined>
            <v-icon left small>mdi-clock-outline</v-icon>
            Focused Date View
          </v-chip>
        </div>
        <p class="text-blue-600 mt-2 text-sm">
          Showing 4 weeks of historical trends leading up to the selected date
          from the Operations Dashboard timeline
        </p>
      </div>

      <!-- Metrics Overview Cards -->
      <div
        v-if="latestMetrics"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        <!-- GMB Stats Card -->
        <div class="bg-white rounded-lg shadow-sm p-6">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center">
              <v-icon color="blue" class="mr-2">mdi-chart-line</v-icon>
              <h4 class="text-lg font-semibold">GMB Stats</h4>
            </div>
            <v-chip small color="blue" outlined>Latest</v-chip>
          </div>
          <div class="space-y-3">
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-600">Maps Impressions</span>
              <span class="font-semibold">{{
                formatNumber(latestMetrics.aggregated.maps_impressions || 0)
              }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-600">Search Impressions</span>
              <span class="font-semibold">{{
                formatNumber(latestMetrics.aggregated.search_impressions || 0)
              }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-600">Directions</span>
              <span class="font-semibold">{{
                formatNumber(latestMetrics.aggregated.directions || 0)
              }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-600">Website Clicks</span>
              <span class="font-semibold">{{
                formatNumber(latestMetrics.aggregated.website_clicks || 0)
              }}</span>
            </div>
          </div>
        </div>

        <!-- Ranking Card -->
        <div class="bg-white rounded-lg shadow-sm p-6">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center">
              <v-icon color="green" class="mr-2">mdi-trophy</v-icon>
              <h4 class="text-lg font-semibold">Rankings</h4>
            </div>
            <v-chip small color="green" outlined>Latest</v-chip>
          </div>
          <div class="space-y-3">
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-600">Average Rank</span>
              <span class="font-semibold">{{
                (latestMetrics.aggregated.rank || 0).toFixed(1)
              }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-600">Total Stores</span>
              <span class="font-semibold">{{
                latestMetrics.aggregated.total_stores || 0
              }}</span>
            </div>
          </div>
        </div>

        <!-- Posts & Reviews Card -->
        <div class="bg-white rounded-lg shadow-sm p-6">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center">
              <v-icon color="orange" class="mr-2">mdi-post-outline</v-icon>
              <h4 class="text-lg font-semibold">Content</h4>
            </div>
            <v-chip small color="orange" outlined>Latest</v-chip>
          </div>
          <div class="space-y-3">
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-600">Total Posts</span>
              <span class="font-semibold">{{
                formatNumber(latestMetrics.aggregated.posts || 0)
              }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-600">Total Reviews</span>
              <span class="font-semibold">{{
                formatNumber(latestMetrics.aggregated.reviews || 0)
              }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-600">Average Rating</span>
              <span class="font-semibold"
                >{{ (latestMetrics.aggregated.rating || 0).toFixed(1) }}⭐</span
              >
            </div>
          </div>
        </div>

        <!-- Engagement Card -->
        <div class="bg-white rounded-lg shadow-sm p-6">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center">
              <v-icon color="purple" class="mr-2">mdi-account-group</v-icon>
              <h4 class="text-lg font-semibold">Engagement</h4>
            </div>
            <v-chip small color="purple" outlined>Latest</v-chip>
          </div>
          <div class="space-y-3">
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-600">Call Clicks</span>
              <span class="font-semibold">{{
                formatNumber(latestMetrics.aggregated.call_clicks || 0)
              }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-600">Bookings</span>
              <span class="font-semibold">{{
                formatNumber(latestMetrics.aggregated.bookings || 0)
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Trend Charts Section -->
      <div class="space-y-6">
        <!-- GMB Stats Trends -->
        <!-- <div class="bg-white rounded-lg shadow-sm p-6">
          <div class="flex items-center justify-between mb-6">
            <div class="flex items-center">
              <v-icon color="primary" class="mr-2">mdi-trending-up</v-icon>
              <h3 class="text-xl font-semibold">GMB Stats Trends</h3>
            </div>
            <v-btn-toggle
              v-model="selectedGmbMetric"
              mandatory
              class="elevation-0"
              @change="updateGmbChart"
            >
              <v-btn value="maps_impressions" small>Maps</v-btn>
              <v-btn value="search_impressions" small>Search</v-btn>
              <v-btn value="directions" small>Directions</v-btn>
              <v-btn value="website_clicks" small>Clicks</v-btn>
            </v-btn-toggle>
          </div>
          <div class="h-64">
            <canvas ref="gmbTrendChart"></canvas>
          </div>
        </div> -->

        <!-- Ranking Trends -->
        <!-- <div class="bg-white rounded-lg shadow-sm p-6">
          <div class="flex items-center justify-between mb-6">
            <div class="flex items-center">
              <v-icon color="success" class="mr-2">mdi-trophy-award</v-icon>
              <h3 class="text-xl font-semibold">Ranking Trends</h3>
            </div>
          </div>
          <div class="h-64">
            <canvas ref="rankTrendChart"></canvas>
          </div>
        </div> -->

        <!-- Content & Reviews Trends -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Posts Trend -->
          <div class="bg-white rounded-lg shadow-sm p-6">
            <div class="flex items-center mb-6">
              <v-icon color="orange" class="mr-2">mdi-post-outline</v-icon>
              <h3 class="text-xl font-semibold">Posts Trend</h3>
            </div>
            <div class="h-48">
              <canvas ref="postsTrendChart"></canvas>
            </div>
          </div>

          <!-- Reviews Trend -->
          <div class="bg-white rounded-lg shadow-sm p-6">
            <div class="flex items-center mb-6">
              <v-icon color="amber" class="mr-2">mdi-star-outline</v-icon>
              <h3 class="text-xl font-semibold">Reviews & Rating Trend</h3>
            </div>
            <div class="h-48">
              <canvas ref="reviewsTrendChart"></canvas>
            </div>
          </div>
        </div>

        <!-- Combined Overview Chart -->
        <div class="bg-white rounded-lg shadow-sm p-6">
          <div class="flex items-center justify-between mb-6">
            <div class="flex items-center">
              <v-icon color="indigo" class="mr-2">mdi-chart-multiple</v-icon>
              <h3 class="text-xl font-semibold">Combined Metrics Overview</h3>
            </div>
            <div class="flex items-center gap-2">
              <!-- User/Store Selection Dropdown -->
              <div class="relative">
                <v-btn
                  small
                  outlined
                  color="primary"
                  @click="toggleUserDropdown"
                >
                  <v-icon left small>mdi-account-multiple</v-icon>
                  {{ getSelectedUsersLabel }}
                </v-btn>

                <!-- Custom Hierarchical Dropdown -->
                <div
                  v-if="showUserDropdown"
                  class="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50"
                  style="max-height: 500px; overflow-y: auto"
                >
                  <!-- Search Bar -->
                  <div class="sticky top-0 bg-white border-b p-3">
                    <input
                      v-model="searchText"
                      type="text"
                      placeholder="Search users or stores..."
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      @input="filterHierarchy"
                    />
                  </div>

                  <!-- Select All -->
                  <div class="p-3 border-b">
                    <label
                      class="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded"
                    >
                      <input
                        type="checkbox"
                        :checked="allSelected"
                        @change="toggleSelectAll"
                        class="mr-2"
                      />
                      <span class="font-semibold">Select All</span>
                    </label>
                  </div>

                  <!-- Hierarchical User/Store List -->
                  <div class="p-2">
                    <div
                      v-for="user in filteredHierarchy"
                      :key="user.uid"
                      class="mb-2"
                    >
                      <!-- User Level -->
                      <div
                        class="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                        @click="toggleUser(user.uid)"
                      >
                        <v-icon small class="mr-1"
                          >mdi-chevron-{{
                            isUserExpanded(user.uid) ? "down" : "right"
                          }}</v-icon
                        >
                        <input
                          type="checkbox"
                          :checked="isUserSelected(user.uid)"
                          @click.stop
                          @change="toggleUserSelection(user.uid)"
                          class="mr-2"
                        />
                        <v-icon small color="primary" class="mr-2"
                          >mdi-account</v-icon
                        >
                        <div class="flex-1">
                          <span class="font-semibold">{{
                            user.displayName
                          }}</span>
                          <span v-if="user.email" class="text-gray-500 text-xs ml-2">{{
                            user.email
                          }}</span>
                          <span class="text-gray-500 text-sm ml-2"
                            >({{ getUserStoreCount(user) }} stores)</span
                          >
                        </div>
                      </div>

                      <!-- Store Level (Nested) -->
                      <div v-if="isUserExpanded(user.uid)" class="ml-8">
                        <div
                          v-for="admin in user.adminWorkspaces"
                          :key="admin.wid || admin.id"
                        >
                          <!-- Admin Workspace Label -->
                          <div v-if="admin.title || admin.brandName" class="ml-2 mb-1">
                            <span class="text-xs text-gray-600 font-medium">
                              {{ admin.title || admin.brandName || 'Admin Workspace' }}
                            </span>
                          </div>
                          <div
                            v-for="store in admin.storeWorkspaces"
                            :key="store.wid || store.id"
                            class="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer ml-4"
                            @click="toggleStoreSelection(user.uid, store.wid || store.id)"
                          >
                            <input
                              type="checkbox"
                              :checked="isStoreSelected(user.uid, store.wid || store.id)"
                              @click.stop
                              @change="
                                toggleStoreSelection(user.uid, store.wid || store.id)
                              "
                              class="mr-2"
                            />
                            <v-icon small color="secondary" class="mr-2"
                              >mdi-store</v-icon
                            >
                            <span>{{
                              store.title ||
                              store.displayName ||
                              store.short_address ||
                              `Store ${store.wid || store.id}`
                            }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Apply Button -->
                  <div class="sticky bottom-0 bg-white border-t p-3">
                    <v-btn
                      small
                      color="primary"
                      block
                      @click="applyChartSelection"
                    >
                      Apply Selection
                    </v-btn>
                  </div>
                </div>
              </div>

              <v-btn
                small
                outlined
                color="primary"
                @click="toggleNormalization"
              >
                {{ isNormalized ? "Show Raw Values" : "Normalize Values" }}
              </v-btn>
            </div>
          </div>
          <div class="h-80">
            <canvas ref="combinedTrendChart"></canvas>
          </div>
        </div>
      </div>

      <!-- Data Table -->
      <div class="bg-white rounded-lg shadow-sm p-6">
        <div class="flex items-center justify-between mb-6">
          <div class="flex items-center">
            <v-icon color="primary" class="mr-2">mdi-table</v-icon>
            <h3 class="text-xl font-semibold">Historical Data Table</h3>
          </div>
          <v-btn
            small
            outlined
            color="primary"
            @click="exportData"
            :loading="exporting"
          >
            <v-icon left small>mdi-export</v-icon>
            Export CSV
          </v-btn>
        </div>

        <v-data-table
          :headers="tableHeaders"
          :items="tableData"
          :items-per-page="10"
          class="elevation-0"
          :loading="loading"
          loading-text="Loading historical data..."
        >
          <template v-slot:[`item.fetched_date`]="{ item }">
            <span class="font-mono text-sm">{{
              formatDate(item.fetched_date)
            }}</span>
          </template>

          <template v-slot:[`item.maps_impressions`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span class="font-semibold cursor-help" v-bind="attrs" v-on="on">{{
                  formatNumber(item.maps_impressions || 0)
                }}</span>
              </template>
              <div
                v-if="
                  item.userBreakdown &&
                  Object.keys(item.userBreakdown).length > 0
                "
              >
                <div class="font-bold mb-1">User Breakdown:</div>
                <div
                  v-for="(data, userName) in item.userBreakdown"
                  :key="userName"
                >
                  {{ userName }}: {{ formatNumber(data.maps_impressions) }}
                </div>
              </div>
            </v-tooltip>
          </template>

          <template v-slot:[`item.search_impressions`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span class="font-semibold cursor-help" v-bind="attrs" v-on="on">{{
                  formatNumber(item.search_impressions || 0)
                }}</span>
              </template>
              <div
                v-if="
                  item.userBreakdown &&
                  Object.keys(item.userBreakdown).length > 0
                "
              >
                <div class="font-bold mb-1">User Breakdown:</div>
                <div
                  v-for="(data, userName) in item.userBreakdown"
                  :key="userName"
                >
                  {{ userName }}: {{ formatNumber(data.search_impressions) }}
                </div>
              </div>
            </v-tooltip>
          </template>

          <template v-slot:[`item.rank`]="{ item }">
            <v-chip
              small
              :color="getRankColor(item.rank)"
              :text-color="
                getRankColor(item.rank) === 'red' ? 'white' : 'black'
              "
            >
              {{ (item.rank || 0).toFixed(1) }}
            </v-chip>
          </template>

          <template v-slot:[`item.rating`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="flex items-center cursor-help"
                  v-bind="attrs"
                  v-on="on"
                >
                  <span class="font-semibold mr-1">{{
                    (item.rating || 0).toFixed(1)
                  }}</span>
                  <v-icon small color="amber">mdi-star</v-icon>
                </div>
              </template>
              <div
                v-if="
                  item.userBreakdown &&
                  Object.keys(item.userBreakdown).length > 0
                "
              >
                <div class="font-bold mb-1">User Ratings:</div>
                <div
                  v-for="(data, userName) in item.userBreakdown"
                  :key="userName"
                >
                  {{ userName }}: {{ data.rating.toFixed(1) }}⭐
                </div>
              </div>
            </v-tooltip>
          </template>

          <template v-slot:[`item.posts`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span
                  class="font-semibold cursor-help"
                  v-bind="attrs"
                  v-on="on"
                  >{{ formatNumber(item.posts || 0) }}</span
                >
              </template>
              <div
                v-if="
                  item.userBreakdown &&
                  Object.keys(item.userBreakdown).length > 0
                "
              >
                <div class="font-bold mb-1">User Posts:</div>
                <div
                  v-for="(data, userName) in item.userBreakdown"
                  :key="userName"
                >
                  {{ userName }}: {{ formatNumber(data.posts) }}
                </div>
              </div>
            </v-tooltip>
          </template>

          <template v-slot:[`item.reviews`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span
                  class="font-semibold cursor-help"
                  v-bind="attrs"
                  v-on="on"
                  >{{ formatNumber(item.reviews || 0) }}</span
                >
              </template>
              <div
                v-if="
                  item.userBreakdown &&
                  Object.keys(item.userBreakdown).length > 0
                "
              >
                <div class="font-bold mb-1">User Reviews:</div>
                <div
                  v-for="(data, userName) in item.userBreakdown"
                  :key="userName"
                >
                  {{ userName }}: {{ formatNumber(data.reviews) }}
                </div>
              </div>
            </v-tooltip>
          </template>

          <template v-slot:[`item.website_clicks`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span
                  class="font-semibold cursor-help"
                  v-bind="attrs"
                  v-on="on"
                  >{{ formatNumber(item.website_clicks || 0) }}</span
                >
              </template>
              <div
                v-if="
                  item.userBreakdown &&
                  Object.keys(item.userBreakdown).length > 0
                "
              >
                <div class="font-bold mb-1">User Clicks:</div>
                <div
                  v-for="(data, userName) in item.userBreakdown"
                  :key="userName"
                >
                  {{ userName }}: {{ formatNumber(data.website_clicks) }}
                </div>
              </div>
            </v-tooltip>
          </template>

          <template v-slot:[`item.directions`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span
                  class="font-semibold cursor-help"
                  v-bind="attrs"
                  v-on="on"
                  >{{ formatNumber(item.directions || 0) }}</span
                >
              </template>
              <div
                v-if="
                  item.userBreakdown &&
                  Object.keys(item.userBreakdown).length > 0
                "
              >
                <div class="font-bold mb-1">User Directions:</div>
                <div
                  v-for="(data, userName) in item.userBreakdown"
                  :key="userName"
                >
                  {{ userName }}: {{ formatNumber(data.directions) }}
                </div>
              </div>
            </v-tooltip>
          </template>
        </v-data-table>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import {
  fetchMultiUserHistoricalMetrics,
  getPresetDateRange,
  exportMetricsToCSV,
  formatDateToDDMMMYYYY,
} from "@/utils/historical-metrics.utils";

export default {
  name: "HistoricalMetricsDashboard",
  props: {
    selectedUsers: {
      type: Array,
      default: () => [],
    },
    selectedDate: {
      type: String,
      default: null,
    },
    userPostsData: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      exporting: false,
      historicalData: [],
      userWiseData: {},
      selectedTimeRange: "30d",
      customStartDate: "",
      customEndDate: "",
      selectedGmbMetric: "maps_impressions",
      isNormalized: false,

      // User selection for charts
      showUserDropdown: false,
      selectedChartUsers: [],
      selectedChartStores: {},
      userHierarchy: [],
      searchText: "",
      filteredHierarchy: [],

      // Chart instances
      gmbTrendChart: null,
      rankTrendChart: null,
      postsTrendChart: null,
      reviewsTrendChart: null,
      combinedTrendChart: null,

      // Table configuration
      tableHeaders: [
        { text: "Date", value: "fetched_date", sortable: true },
        { text: "Maps Impressions", value: "maps_impressions", sortable: true },
        {
          text: "Search Impressions",
          value: "search_impressions",
          sortable: true,
        },
        { text: "Directions", value: "directions", sortable: true },
        { text: "Website Clicks", value: "website_clicks", sortable: true },
        { text: "Call Clicks", value: "call_clicks", sortable: true },
        { text: "Bookings", value: "bookings", sortable: true },
        { text: "Avg Rank", value: "rank", sortable: true },
        { text: "Posts", value: "posts", sortable: true },
        { text: "Reviews", value: "reviews", sortable: true },
        { text: "Rating", value: "rating", sortable: true },
        { text: "Total Stores", value: "total_stores", sortable: true },
      ],
    };
  },

  computed: {
    latestMetrics() {
      return this.historicalData.length > 0 ? this.historicalData[0] : null;
    },

    getSelectedUsersLabel() {
      const userCount = this.selectedChartUsers.length;
      const storeCount = Object.values(this.selectedChartStores).reduce(
        (sum, stores) => sum + stores.length,
        0
      );

      if (userCount === 0 && storeCount === 0) {
        return "All Users";
      } else if (storeCount === 0) {
        return `${userCount} User${userCount !== 1 ? "s" : ""}`;
      } else {
        return `${userCount} User${
          userCount !== 1 ? "s" : ""
        }, ${storeCount} Store${storeCount !== 1 ? "s" : ""}`;
      }
    },

    allSelected() {
      if (!this.userHierarchy || this.userHierarchy.length === 0) return false;

      return this.userHierarchy.every((user) => {
        if (!this.isUserSelected(user.uid)) return false;
        const stores = this.getAllStoresForUser(user);
        return stores.every((store) =>
          this.isStoreSelected(user.uid, store.wid || store.id)
        );
      });
    },

    expandedUsers() {
      return this.selectedChartUsers;
    },

    dateRange() {
      if (this.historicalData.length === 0) return 0;

      const dates = this.historicalData
        .map((d) => {
          // Handle Firebase timestamps
          if (d.created_at?.toDate) {
            return d.created_at.toDate();
          } else if (d.created_at?.seconds) {
            return new Date(d.created_at.seconds * 1000);
          } else {
            return new Date(d.fetched_date);
          }
        })
        .sort((a, b) => a - b);

      const diffTime = Math.abs(dates[dates.length - 1] - dates[0]);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    },

    totalStores() {
      return this.latestMetrics?.aggregated?.total_stores || 0;
    },

    lastUpdated() {
      if (!this.latestMetrics?.updated_at) return "N/A";
      return formatDateToDDMMMYYYY(this.latestMetrics.updated_at);
    },

    tableData() {
      return this.historicalData.map((item) => ({
        fetched_date: item.fetched_date,
        ...item.aggregated,
        // Add user-wise data for hover display
        userBreakdown: this.getUserBreakdownForDate(item.fetched_date),
      }));
    },
  },

  watch: {
    selectedUsers: {
      handler(newVal) {
        this.fetchHistoricalData();
      },
      immediate: true,
    },
    selectedDate: {
      handler: "onSelectedDateChange",
      immediate: false,
    },
    userPostsData: {
      handler(newVal) {
        this.initializeUserHierarchy();
      },
      immediate: true,
      deep: true,
    },
  },

  beforeDestroy() {
    this.destroyAllCharts();
  },

  methods: {
    // User Hierarchy Management Methods
    initializeUserHierarchy() {
      // If userPostsData is available, use it for hierarchy
      console.log("Initializing user hierarchy...", this.userPostsData);
      if (this.userPostsData && this.userPostsData.length > 0) {
        this.userHierarchy = this.userPostsData.map((user) => ({
          uid: user.uid,
          displayName: user.displayName || user.email || `User ${user.uid.substring(0, 8)}`,
          email: user.email || "",
          adminWorkspaces: user.adminWorkspaces || [],
        }));
      } else if (this.selectedUsers && this.selectedUsers.length > 0) {
        // Fallback: Create basic hierarchy from selectedUsers
        this.userHierarchy = this.selectedUsers.map((userId) => ({
          uid: userId,
          displayName: `User ${userId.substring(0, 8)}`,
          email: "",
          adminWorkspaces: [],
        }));
      } else {
        this.userHierarchy = [];
        return;
      }

      // Initialize all users and stores as selected by default
      this.selectedChartUsers = this.userHierarchy.map((u) => u.uid);
      this.selectedChartStores = {};

      this.userHierarchy.forEach((user) => {
        const stores = this.getAllStoresForUser(user);
        if (stores.length > 0) {
          this.selectedChartStores[user.uid] = stores.map((s) => s.wid);
        }
      });

      this.filteredHierarchy = [...this.userHierarchy];
    },

    getAllStoresForUser(user) {
      const stores = [];
      if (user.adminWorkspaces && Array.isArray(user.adminWorkspaces)) {
        user.adminWorkspaces.forEach((admin) => {
          if (admin.storeWorkspaces && Array.isArray(admin.storeWorkspaces)) {
            // Ensure each store has an id
            const storesWithIds = admin.storeWorkspaces.map(store => ({
              ...store,
              wid: store.wid || store.id,
              id: store.id || store.wid
            }));
            stores.push(...storesWithIds);
          }
        });
      }
      return stores;
    },

    getUserStoreCount(user) {
      return this.getAllStoresForUser(user).length;
    },

    toggleUserDropdown() {
      this.showUserDropdown = !this.showUserDropdown;
    },

    toggleUser(userId) {
      const index = this.selectedChartUsers.indexOf(userId);
      if (index >= 0) {
        this.selectedChartUsers.splice(index, 1);
      } else {
        this.selectedChartUsers.push(userId);
      }
    },

    isUserExpanded(userId) {
      return this.expandedUsers.includes(userId);
    },

    isUserSelected(userId) {
      return this.selectedChartUsers.includes(userId);
    },

    isStoreSelected(userId, storeId) {
      return this.selectedChartStores[userId]?.includes(storeId) || false;
    },

    toggleUserSelection(userId) {
      const user = this.userHierarchy.find((u) => u.uid === userId);
      if (!user) return;

      const isSelected = this.isUserSelected(userId);

      if (isSelected) {
        // Deselect user and all its stores
        const index = this.selectedChartUsers.indexOf(userId);
        if (index >= 0) {
          this.selectedChartUsers.splice(index, 1);
        }
        delete this.selectedChartStores[userId];
      } else {
        // Select user and all its stores
        this.selectedChartUsers.push(userId);
        const stores = this.getAllStoresForUser(user);
        if (stores.length > 0) {
          this.$set(
            this.selectedChartStores,
            userId,
            stores.map((s) => s.wid || s.id)
          );
        }
      }
    },

    toggleStoreSelection(userId, storeId) {
      if (!this.selectedChartStores[userId]) {
        this.$set(this.selectedChartStores, userId, []);
      }

      const stores = this.selectedChartStores[userId];
      const index = stores.indexOf(storeId);

      if (index >= 0) {
        stores.splice(index, 1);
        if (stores.length === 0) {
          // If no stores selected, deselect user
          const userIndex = this.selectedChartUsers.indexOf(userId);
          if (userIndex >= 0) {
            this.selectedChartUsers.splice(userIndex, 1);
          }
        }
      } else {
        stores.push(storeId);
        // If store selected but user not selected, select user
        if (!this.selectedChartUsers.includes(userId)) {
          this.selectedChartUsers.push(userId);
        }
      }
    },

    toggleSelectAll() {
      if (this.allSelected) {
        // Deselect all
        this.selectedChartUsers = [];
        this.selectedChartStores = {};
      } else {
        // Select all
        this.selectedChartUsers = this.userHierarchy.map((u) => u.uid);
        this.selectedChartStores = {};

        this.userHierarchy.forEach((user) => {
          const stores = this.getAllStoresForUser(user);
          if (stores.length > 0) {
            this.selectedChartStores[user.uid] = stores.map((s) => s.wid || s.id);
          }
        });
      }
    },

    filterHierarchy() {
      if (!this.searchText) {
        this.filteredHierarchy = [...this.userHierarchy];
        return;
      }

      const searchLower = this.searchText.toLowerCase();
      this.filteredHierarchy = this.userHierarchy.filter((user) => {
        // Check if user matches
        if (
          user.displayName?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower)
        ) {
          return true;
        }

        // Check if any admin workspace or store matches
        if (user.adminWorkspaces && Array.isArray(user.adminWorkspaces)) {
          return user.adminWorkspaces.some((admin) => {
            // Check admin workspace name
            if (
              admin.title?.toLowerCase().includes(searchLower) ||
              admin.brandName?.toLowerCase().includes(searchLower)
            ) {
              return true;
            }
            
            // Check store names
            if (admin.storeWorkspaces && Array.isArray(admin.storeWorkspaces)) {
              return admin.storeWorkspaces.some(
                (store) =>
                  store.title?.toLowerCase().includes(searchLower) ||
                  store.displayName?.toLowerCase().includes(searchLower) ||
                  store.short_address?.toLowerCase().includes(searchLower)
              );
            }
            return false;
          });
        }
        
        return false;
      });
    },

    applyChartSelection() {
      this.showUserDropdown = false;
      // Re-initialize charts with new selection
      this.initializeAllCharts();
    },

    getUserBreakdownForDate(date) {
      const breakdown = {};

      this.selectedChartUsers.forEach((userId) => {
        const user = this.userHierarchy.find((u) => u.uid === userId);
        if (!user) return;

        const userRecord = this.userWiseData[userId]?.find(
          (r) => r.fetched_date === date
        );
        if (userRecord && userRecord.aggregated) {
          breakdown[user.displayName] = {
            posts: userRecord.aggregated.posts || 0,
            reviews: userRecord.aggregated.reviews || 0,
            rating: userRecord.aggregated.rating || 0,
            maps_impressions: userRecord.aggregated.maps_impressions || 0,
            search_impressions: userRecord.aggregated.search_impressions || 0,
            website_clicks: userRecord.aggregated.website_clicks || 0,
            directions: userRecord.aggregated.directions || 0,
            total_calls: userRecord.aggregated.total_calls || 0,
          };
        }
      });

      return breakdown;
    },

    async fetchHistoricalData() {
      if (this.selectedUsers.length === 0) {
        this.historicalData = [];
        return;
      }

      this.loading = true;
      try {
        // If a specific date is selected, get data for previous weeks to show trends
        let startDate, endDate;
        if (this.selectedDate) {
          // Use the selected date as the end point and get 4 weeks of data before it
          const selectedDateObj = new Date(this.selectedDate);
          const fourWeeksBefore = new Date(selectedDateObj);
          fourWeeksBefore.setDate(fourWeeksBefore.getDate() - 28); // 4 weeks = 28 days

          startDate = fourWeeksBefore.toISOString().split("T")[0];
          endDate = selectedDateObj.toISOString().split("T")[0];
        } else {
          // Use the default time range
          const dateRange = this.getDateRange();
          startDate = dateRange.startDate;
          endDate = dateRange.endDate;
        }

        console.log(
          `Fetching historical data for date range: ${startDate} to ${endDate}`
        );

        const result = await fetchMultiUserHistoricalMetrics(
          this.selectedUsers,
          startDate,
          endDate,
          100 // limit per user
        );

        console.log(`Historical data fetch result:`, result);
        console.log(`Date range requested: ${startDate} to ${endDate}`);
        console.log(`Data received:`, result.data);

        if (result.success) {
          // Aggregate all data
          this.historicalData = result.data;

          // Also organize data by user for bifurcated display
          this.organizeDataByUser(result.data);

          if (result.errors && result.errors.length > 0) {
            console.warn("Some users had errors:", result.errors);
            this.$snackbar.show(
              `Data loaded with ${result.errors.length} user(s) having errors`,
              "warning"
            );
          }
        } else {
          throw new Error(result.error || "Failed to fetch historical data");
        }

        // Initialize charts after data is loaded
        this.$nextTick(() => {
          this.initializeAllCharts();
        });
      } catch (error) {
        console.error("Error fetching historical data:", error);
        this.$snackbar.show("Error loading historical data", "error");
      } finally {
        this.loading = false;
      }
    },

    getDateRange() {
      if (this.selectedTimeRange === "custom") {
        return {
          startDate:
            this.customStartDate || new Date().toISOString().split("T")[0],
          endDate: this.customEndDate || new Date().toISOString().split("T")[0],
        };
      } else {
        return getPresetDateRange(this.selectedTimeRange);
      }
    },

    handleTimeRangeChange() {
      this.fetchHistoricalData();
    },

    handleCustomDateChange() {
      if (this.customStartDate && this.customEndDate) {
        this.fetchHistoricalData();
      }
    },

    refreshData() {
      this.fetchHistoricalData();
    },

    organizeDataByUser(data) {
      // Organize historical data by user ID
      this.userWiseData = {};

      // Initialize userWiseData for all selected users (including fallback ones)
      this.selectedUsers.forEach((userId) => {
        this.userWiseData[userId] = [];
      });

      data.forEach((record) => {
        const userId = record.userId;
        if (!userId) return;

        if (!this.userWiseData[userId]) {
          this.userWiseData[userId] = [];
        }

        this.userWiseData[userId].push(record);
      });

      // Sort each user's data by date
      Object.keys(this.userWiseData).forEach((userId) => {
        if (this.userWiseData[userId].length > 0) {
          this.userWiseData[userId].sort((a, b) => {
            const dateA = new Date(a.fetched_date);
            const dateB = new Date(b.fetched_date);
            return dateB - dateA;
          });
        }
      });
    },

    getFilteredData() {
      // Get data filtered by selected users and stores
      const filteredData = [];

      this.selectedChartUsers.forEach((userId) => {
        const userData = this.userWiseData[userId];
        if (!userData) return;

        // For now, include all user data if user is selected
        // TODO: Filter by selected stores if needed
        filteredData.push(...userData);
      });

      // Sort by date
      filteredData.sort((a, b) => {
        const dateA = new Date(a.fetched_date);
        const dateB = new Date(b.fetched_date);
        return dateB - dateA;
      });

      return filteredData;
    },

    onSelectedDateChange() {
      console.log(
        `Historical dashboard: Selected date changed to: ${this.selectedDate}`
      );
      if (this.selectedDate && this.selectedUsers.length > 0) {
        console.log(
          `Refreshing historical data for selected date: ${this.selectedDate}`
        );
        // Clear existing data to show loading state
        this.historicalData = [];
        // Fetch new data for the selected date
        this.fetchHistoricalData();
      } else if (!this.selectedDate) {
        console.log("No specific date selected, using default time range");
        this.fetchHistoricalData();
      }
    },

    // Chart initialization methods
    initializeAllCharts() {
      this.destroyAllCharts();

      this.$nextTick(() => {
        this.initializeGmbTrendChart();
        this.initializeRankTrendChart();
        this.initializePostsTrendChart();
        this.initializeReviewsTrendChart();
        this.initializeCombinedChart();
      });
    },

    destroyAllCharts() {
      [
        this.gmbTrendChart,
        this.rankTrendChart,
        this.postsTrendChart,
        this.reviewsTrendChart,
        this.combinedTrendChart,
      ].forEach((chart) => {
        if (chart) {
          chart.destroy();
        }
      });
    },

    initializeGmbTrendChart() {
      const ctx = this.$refs.gmbTrendChart?.getContext("2d");
      if (!ctx || this.historicalData.length === 0) return;

      const labels = this.historicalData
        .slice()
        .reverse()
        .map((d) => this.formatDateShort(d.fetched_date));

      const data = this.historicalData
        .slice()
        .reverse()
        .map((d) => d.aggregated?.[this.selectedGmbMetric] || 0);

      this.gmbTrendChart = new Chart(ctx, {
        type: "line",
        data: {
          labels,
          datasets: [
            {
              label: this.getMetricLabel(this.selectedGmbMetric),
              data,
              borderColor: "#4F46E5",
              backgroundColor: "#4F46E520",
              fill: true,
              tension: 0,
              pointRadius: 4,
              pointHoverRadius: 6,
              borderWidth: 3,
            },
          ],
        },
        options: this.getDefaultChartOptions(),
      });
    },

    initializeRankTrendChart() {
      const ctx = this.$refs.rankTrendChart?.getContext("2d");
      if (!ctx || this.historicalData.length === 0) return;

      const labels = this.historicalData
        .slice()
        .reverse()
        .map((d) => this.formatDateShort(d.fetched_date));

      const data = this.historicalData
        .slice()
        .reverse()
        .map((d) => d.aggregated?.rank || 0);

      this.rankTrendChart = new Chart(ctx, {
        type: "line",
        data: {
          labels,
          datasets: [
            {
              label: "Average Rank",
              data,
              borderColor: "#10B981",
              backgroundColor: "#10B98120",
              fill: true,
              tension: 0,
              pointRadius: 4,
              pointHoverRadius: 6,
              borderWidth: 3,
            },
          ],
        },
        options: {
          ...this.getDefaultChartOptions(),
          scales: {
            ...this.getDefaultChartOptions().scales,
            y: {
              ...this.getDefaultChartOptions().scales.y,
              reverse: true, // Lower rank numbers are better
            },
          },
        },
      });
    },

    initializePostsTrendChart() {
      const ctx = this.$refs.postsTrendChart?.getContext("2d");
      if (!ctx || this.historicalData.length === 0) return;

      // Get unique dates from all data
      const allDates = new Set();

      // First collect dates from userWiseData
      Object.values(this.userWiseData).forEach((userData) => {
        userData.forEach((record) => {
          allDates.add(record.fetched_date);
        });
      });

      // If no dates found in userWiseData, fallback to historicalData
      if (allDates.size === 0) {
        this.historicalData.forEach((record) => {
          if (record.fetched_date) {
            allDates.add(record.fetched_date);
          }
        });
      }

      const sortedDates = Array.from(allDates).sort(
        (a, b) => new Date(a) - new Date(b)
      );
      const labels = sortedDates.map((d) => this.formatDateShort(d));

      // Create datasets for each selected user
      const datasets = [];
      const colors = [
        "#FF6384",
        "#36A2EB",
        "#FFCE56",
        "#4BC0C0",
        "#9966FF",
        "#FF9F40",
      ];
      let colorIndex = 0;

      // Add individual user datasets
      this.selectedChartUsers.forEach((userId) => {
        const user = this.userHierarchy.find((u) => u.uid === userId);
        if (!user) return;

        const userData = sortedDates.map((date) => {
          const record = this.userWiseData[userId]?.find(
            (r) => r.fetched_date === date
          );
          return record?.aggregated?.posts || 0;
        });

        const color = colors[colorIndex % colors.length];
        datasets.push({
          label: user.displayName, // Keep for tooltip but hide in legend
          data: userData,
          backgroundColor: color + "80", // Add transparency
          borderColor: color,
          borderWidth: 1,
          barPercentage: 0.4,
          categoryPercentage: 0.6,
          hidden: this.selectedChartUsers.length > 3, // Hide by default if many users
        });
        colorIndex++;
      });

      this.postsTrendChart = new Chart(ctx, {
        type: "bar",
        data: {
          labels,
          datasets,
        },
        options: {
          ...this.getDefaultChartOptions(),
          plugins: {
            ...this.getDefaultChartOptions().plugins,
            legend: {
              display: false, // Hide legend for posts trend
            },
            tooltip: {
              mode: "index",
              intersect: false,
              callbacks: {
                title: function (context) {
                  return `Date: ${context[0].label}`;
                },
                label: function (context) {
                  const label = context.dataset.label || "";
                  const value = context.parsed.y || 0;
                  // Remove "User " prefix if present
                  const displayName = label.startsWith("User ")
                    ? label.substring(5)
                    : label;
                  return `${displayName}: ${value} posts`;
                },
                afterBody: function (context) {
                  if (context.length <= 1) return; // Only show total if multiple users

                  // Calculate total for this date
                  let total = 0;
                  context.forEach((item) => {
                    total += item.parsed.y || 0;
                  });

                  return `\nTotal: ${total} posts`;
                },
              },
            },
          },
          scales: {
            ...this.getDefaultChartOptions().scales,
            x: {
              ...this.getDefaultChartOptions().scales.x,
              stacked: false,
              grid: {
                display: true,
                color: "#E5E7EB",
              },
            },
            y: {
              ...this.getDefaultChartOptions().scales.y,
              stacked: false,
              title: {
                display: true,
                text: "Number of Posts",
              },
            },
          },
        },
      });
    },

    initializeReviewsTrendChart() {
      const ctx = this.$refs.reviewsTrendChart?.getContext("2d");
      if (!ctx || this.historicalData.length === 0) return;

      // Get unique dates from all data
      const allDates = new Set();

      // First collect dates from userWiseData
      Object.values(this.userWiseData).forEach((userData) => {
        userData.forEach((record) => {
          allDates.add(record.fetched_date);
        });
      });

      // If no dates found in userWiseData, fallback to historicalData
      if (allDates.size === 0) {
        this.historicalData.forEach((record) => {
          if (record.fetched_date) {
            allDates.add(record.fetched_date);
          }
        });
      }

      const sortedDates = Array.from(allDates).sort(
        (a, b) => new Date(a) - new Date(b)
      );
      const labels = sortedDates.map((d) => this.formatDateShort(d));

      const datasets = [];

      // Calculate aggregated reviews for all selected users
      const aggregatedReviews = sortedDates.map((date) => {
        let total = 0;
        this.selectedChartUsers.forEach((userId) => {
          const record = this.userWiseData[userId]?.find(
            (r) => r.fetched_date === date
          );
          if (record && record.aggregated) {
            total += record.aggregated.reviews || 0;
          }
        });
        return total;
      });

      // Calculate average rating across all selected users
      const aggregatedRatings = sortedDates.map((date) => {
        let totalRating = 0;
        let count = 0;
        this.selectedChartUsers.forEach((userId) => {
          const record = this.userWiseData[userId]?.find(
            (r) => r.fetched_date === date
          );
          if (record && record.aggregated && record.aggregated.rating) {
            totalRating += record.aggregated.rating;
            count++;
          }
        });
        return count > 0 ? Math.max(0, Math.min(5, totalRating / count)) : 0;
      });

      // Add aggregated reviews bar chart
      datasets.push({
        label: "Reviews",
        data: aggregatedReviews,
        backgroundColor: "#3B82F6" + "80", // Blue with transparency
        borderColor: "#3B82F6",
        borderWidth: 2,
        barThickness: 30,
        maxBarThickness: 40,
        yAxisID: "y",
        dataType: "reviews",
      });

      // Add aggregated rating line chart
      datasets.push({
        label: "Rating",
        data: aggregatedRatings,
        type: "line",
        borderColor: "#10B981", // Green
        backgroundColor: "#10B981",
        fill: false,
        tension: 0,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: "#10B981",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2,
        yAxisID: "y1",
        borderWidth: 3,
        spanGaps: true,
        dataType: "rating",
      });

      this.reviewsTrendChart = new Chart(ctx, {
        type: "bar",
        data: {
          labels,
          datasets,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: "nearest",
            axis: "x",
            intersect: false,
          },
          layout: {
            padding: {
              top: 20, // Minimal top padding
              bottom: 10,
              left: 10,
              right: 10,
            },
          },
          plugins: {
            legend: {
              display: true, // Show legend for Reviews and Rating
              position: "top",
              labels: {
                padding: 15,
                usePointStyle: true,
                font: { size: 12 },
              },
            },
            tooltip: {
              mode: "index",
              intersect: false,
              callbacks: {
                title: function (context) {
                  return `Date: ${context[0].label}`;
                },
                label: function (context) {
                  const label = context.dataset.label || "";
                  const value = context.parsed.y || 0;
                  const dataType = context.dataset.dataType;

                  // if (dataType === "rating") {
                  //   return `${label}: ${value.toFixed(1)}⭐`;
                  // } else if (dataType === "reviews") {
                  //   return `${label}: ${value} reviews`;
                  // }
                  return "";
                },
                filter: (function () {
                  // Don't show duplicate labels
                  const seen = {};
                  return function (item) {
                    const key = item.dataset.label + item.dataset.dataType;
                    if (seen[key]) return false;
                    seen[key] = true;
                    return true;
                  };
                })(),
                afterBody: (context) => {
                  if (context.length === 0) return;

                  // Show user breakdown without header
                  const lines = [];
                  const dataIndex = context[0].dataIndex;
                  const date = sortedDates[dataIndex];

                  this.selectedChartUsers.forEach((userId) => {
                    const user = this.userHierarchy.find(
                      (u) => u.uid === userId
                    );
                    if (!user) return;

                    const record = this.userWiseData[userId]?.find(
                      (r) => r.fetched_date === date
                    );
                    if (record && record.aggregated) {
                      const reviews = record.aggregated.reviews || 0;
                      const rating = record.aggregated.rating || 0;
                      lines.push(
                        `${
                          user.displayName
                        }: ${reviews} reviews, ${rating.toFixed(1)}⭐`
                      );
                    }
                  });

                  return lines.length > 0 ? '\n' + lines.join("\n") : '';
                },
              },
            },
          },
          scales: {
            x: {
              grid: {
                display: true,
                color: "#E5E7EB",
              },
              ticks: {
                font: { size: 10 },
              },
            },
            y: {
              // TODO: store max reviews and replace 400 with it
              max: 400 * 1.5,
              type: "linear",
              display: true,
              position: "left",
              beginAtZero: true,
              title: {
                display: true,
                text: "Reviews Count",
                font: { size: 11 },
              },
              ticks: {
                callback: function (value) {
                  return value >= 1000
                    ? (value / 1000).toFixed(0) + "K"
                    : value;
                },
                font: { size: 10 },
              },
              grid: {
                display: true,
                color: "#E5E7EB",
              },
            },
            y1: {
              type: "linear",
              display: false,
              position: "right",
              min: 0,
              max: 5,
              beginAtZero: true,
              grid: {
                drawOnChartArea: false,
              },
            },
          },
        },
        plugins: [
          {
            id: "ratingLabels",
            afterDatasetsDraw: (chart) => {
              const ctx = chart.ctx;
              const datasets = chart.data.datasets;
              const ratingDataset = datasets.find((d) => d.label === "Rating");

              if (ratingDataset) {
                const ratingDatasetIndex = datasets.indexOf(ratingDataset);
                const meta = chart.getDatasetMeta(ratingDatasetIndex);
                meta.data.forEach((point, index) => {
                  const rating = ratingDataset.data[index];
                  if (rating > 0) {
                    ctx.save();
                    ctx.fillStyle = "#ffffff";
                    ctx.font = "bold 8px Arial";
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";
                    ctx.fillText(rating.toFixed(1), point.x, point.y);
                    ctx.restore();
                  }
                });
              }
            },
          },
          {
            id: "positionRatingLineAboveBars",
            beforeDatasetsDraw(chart) {
              const barMeta = chart.getDatasetMeta(0); // Bar dataset (Reviews)
              const lineMeta = chart.getDatasetMeta(1); // Line dataset (Rating)

              if (barMeta && lineMeta && barMeta.data && lineMeta.data) {
                // Find the highest bar position
                let highestBarY = chart.chartArea.bottom;
                if (barMeta.data && barMeta.data.length > 0) {
                  barMeta.data.forEach((bar) => {
                    if (bar && bar.y < highestBarY) {
                      highestBarY = bar.y;
                    }
                  });
                }

                // Define a rating area that uses available space above bars
                const chartTop = chart.chartArea.top;

                // Calculate space available above the highest bar
                const spaceAboveBars = Math.max(0, highestBarY - chartTop);
                const ratingAreaHeight = Math.min(50, spaceAboveBars * 0.6); // Use 60% of available space above bars
                const bufferFromBars = 15; // Small buffer from bars
                const ratingAreaBottom = Math.max(
                  chartTop + 30,
                  highestBarY - bufferFromBars
                );
                const ratingAreaTop = ratingAreaBottom - ratingAreaHeight;

                // Get rating data and range
                const ratingData = chart.data.datasets[1].data;
                const validRatings = ratingData.filter((r) => r > 0);

                if (validRatings.length === 0) return;

                const minRating = Math.min(...validRatings);
                const maxRating = Math.max(...validRatings);
                const ratingRange = maxRating - minRating || 1;

                // Position each rating point in the dedicated rating area
                lineMeta.data.forEach((point, index) => {
                  const ratingValue = ratingData[index];

                  if (ratingValue > 0) {
                    // Calculate relative position within rating range (0 to 1)
                    const ratingPosition =
                      ratingRange > 0
                        ? (ratingValue - minRating) / ratingRange
                        : 0.5;

                    // Position in the rating area - higher ratings go higher
                    // Invert so higher ratings appear at the top
                    point.y =
                      ratingAreaBottom - ratingPosition * ratingAreaHeight;

                    // Ensure point stays within the rating area bounds
                    point.y = Math.max(
                      ratingAreaTop,
                      Math.min(ratingAreaBottom, point.y)
                    );
                  } else {
                    // Hide points with no rating by moving them far off screen
                    point.y = -1000;
                  }
                });
              }
            },
          },
        ],
      });
    },

    initializeCombinedChart() {
      const ctx = this.$refs.combinedTrendChart?.getContext("2d");
      if (!ctx || this.historicalData.length === 0) return;

      // Get unique dates from selected user data
      const allDates = new Set();
      this.selectedChartUsers.forEach((userId) => {
        const userData = this.userWiseData[userId];
        if (userData) {
          userData.forEach((record) => {
            allDates.add(record.fetched_date);
          });
        }
      });

      const sortedDates = Array.from(allDates).sort(
        (a, b) => new Date(a) - new Date(b)
      );
      const labels = sortedDates.map((d) => this.formatDateShort(d));

      const datasets = [];
      const metrics = [
        { key: "maps_impressions", label: "Maps", color: "#4F46E5" },
        { key: "search_impressions", label: "Search", color: "#10B981" },
        { key: "website_clicks", label: "Clicks", color: "#F59E0B" },
        { key: "directions", label: "Directions", color: "#EF4444" },
      ];

      // Create aggregated datasets for each metric (combining all users)
      metrics.forEach((metric) => {
        const data = sortedDates.map((date) => {
          let total = 0;
          this.selectedChartUsers.forEach((userId) => {
            const record = this.userWiseData[userId]?.find(
              (r) => r.fetched_date === date
            );
            if (record && record.aggregated) {
              total += record.aggregated[metric.key] || 0;
            }
          });
          return this.isNormalized
            ? this.normalizeValue(total, metric.key)
            : total;
        });

        datasets.push({
          label: metric.label,
          data,
          borderColor: metric.color,
          backgroundColor: metric.color + "20",
          fill: false,
          tension: 0,
          pointRadius: 3,
          pointHoverRadius: 5,
          borderWidth: 2,
          hidden: false, // Show all metrics by default
        });
      });

      this.combinedTrendChart = new Chart(ctx, {
        type: "line",
        data: {
          labels,
          datasets,
        },
        options: {
          ...this.getDefaultChartOptions(),
          plugins: {
            ...this.getDefaultChartOptions().plugins,
            tooltip: {
              mode: "index",
              intersect: false,
              callbacks: {
                title: function (context) {
                  return `Date: ${context[0].label}`;
                },
                label: function (context) {
                  // Don't show aggregated values in the main label
                  // User-wise data will be shown in afterBody
                  return '';
                },
                afterBody: (context) => {
                  if (context.length === 0) return;

                  // Show user-wise breakdown with proper formatting
                  const lines = [];
                  const dataIndex = context[0].dataIndex;
                  const date = sortedDates[dataIndex];

                  this.selectedChartUsers.forEach((userId, userIndex) => {
                    const user = this.userHierarchy.find(
                      (u) => u.uid === userId
                    );
                    if (!user) return;

                    const record = this.userWiseData[userId]?.find(
                      (r) => r.fetched_date === date
                    );
                    if (record && record.aggregated) {
                      // Add spacing between users (except for the first one)
                      if (userIndex > 0) {
                        lines.push(''); // Empty line for spacing
                      }
                      
                      // Add user name as header
                      lines.push(`${user.displayName}:`);
                      
                      // Add each metric on its own line
                      metrics.forEach((metric) => {
                        const value = record.aggregated[metric.key] || 0;
                        lines.push(`  ${metric.label}: ${value.toLocaleString()}`);
                      });
                    }
                  });

                  return lines.length > 0 ? "\n" + lines.join("\n") : "";
                },
              },
            },
            legend: {
              display: true, // Show legend for metrics only
              position: "top",
              labels: {
                padding: 15,
                usePointStyle: true,
                font: { size: 12 },
              },
            },
          },
          scales: {
            ...this.getDefaultChartOptions().scales,
            y: {
              ...this.getDefaultChartOptions().scales.y,
              title: {
                display: true,
                text: this.isNormalized ? "Normalized Value (0-100)" : "Count",
              },
            },
          },
        },
      });
    },

    normalizeValue(value, metric) {
      // Normalize values to 0-100 scale based on metric type
      const maxValues = {
        maps_impressions: 10000,
        search_impressions: 5000,
        website_clicks: 1000,
        directions: 500,
      };

      const max = maxValues[metric] || 1000;
      return Math.min(100, (value / max) * 100);
    },

    getChartData(metric) {
      const rawData = this.historicalData
        .slice()
        .reverse()
        .map((d) => d.aggregated?.[metric] || 0);

      if (!this.isNormalized) return rawData;

      // Normalize data to 0-100 scale
      const max = Math.max(...rawData);
      const min = Math.min(...rawData);
      const range = max - min;

      if (range === 0) return rawData;

      return rawData.map((value) => ((value - min) / range) * 100);
    },

    updateGmbChart() {
      this.initializeGmbTrendChart();
    },

    toggleNormalization() {
      this.isNormalized = !this.isNormalized;
      // Destroy existing chart before reinitializing
      if (this.combinedTrendChart) {
        this.combinedTrendChart.destroy();
        this.combinedTrendChart = null;
      }
      this.$nextTick(() => {
        this.initializeCombinedChart();
      });
    },

    getDefaultChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "top",
            labels: {
              padding: 20,
              usePointStyle: true,
            },
          },
          tooltip: {
            mode: "index",
            intersect: false,
          },
        },
        scales: {
          x: {
            display: true,
            grid: {
              display: true,
              color: "#E5E7EB",
            },
          },
          y: {
            display: true,
            beginAtZero: true,
            grid: {
              display: true,
              color: "#E5E7EB",
            },
          },
        },
        interaction: {
          mode: "nearest",
          axis: "x",
          intersect: false,
        },
      };
    },

    // Utility methods
    formatNumber(num) {
      return new Intl.NumberFormat().format(num);
    },

    formatDate(dateStr) {
      return formatDateToDDMMMYYYY(dateStr);
    },

    formatDateShort(dateStr) {
      if (!dateStr) return "N/A";

      // Handle Firebase timestamp or date string
      let date;
      if (dateStr.toDate) {
        date = dateStr.toDate();
      } else if (dateStr.seconds) {
        date = new Date(dateStr.seconds * 1000);
      } else {
        date = new Date(dateStr);
      }

      if (isNaN(date.getTime())) return "N/A";

      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      });
    },

    getMetricLabel(metric) {
      const labels = {
        maps_impressions: "Maps Impressions",
        search_impressions: "Search Impressions",
        directions: "Directions",
        website_clicks: "Website Clicks",
        call_clicks: "Call Clicks",
        bookings: "Bookings",
      };
      return labels[metric] || metric;
    },

    getRankColor(rank) {
      if (rank <= 3) return "green";
      if (rank <= 10) return "orange";
      return "red";
    },

    formatFloatingNumber(num, decimals = 1) {
      if (num === null || num === undefined || isNaN(num)) return "0.0";
      return Number(num).toFixed(decimals);
    },

    async exportData() {
      this.exporting = true;
      try {
        const csvContent = this.generateCSV();
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          `historical-metrics-${new Date().toISOString().split("T")[0]}.csv`
        );
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.$snackbar.show("Data exported successfully", "success");
      } catch (error) {
        console.error("Export error:", error);
        this.$snackbar.show("Error exporting data", "error");
      } finally {
        this.exporting = false;
      }
    },

    generateCSV() {
      return exportMetricsToCSV(this.tableData, this.tableHeaders);
    },
  },
};
</script>

<style scoped>
.no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  gap: 16px;
}

.no-data-text {
  font-size: 18px;
  color: #666;
  margin: 0;
  text-align: center;
}

.historical-metrics-dashboard {
  width: 100%;
}

.relative {
  position: relative;
}

.absolute {
  position: absolute;
}

.z-50 {
  z-index: 50;
}

.loading-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Custom scrollbar for data table */
::v-deep .v-data-table__wrapper {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 #f7fafc;
}

::v-deep .v-data-table__wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::v-deep .v-data-table__wrapper::-webkit-scrollbar-track {
  background: #f7fafc;
  border-radius: 4px;
}

::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 4px;
}

::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}
</style>
